// @core/api/client.ts
// Auto-generated by corebuilder. DO NOT EDIT.

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type QueryParams = Record<string, any>;

/**
 * Serialize one query value under `key`, recursing into nested structures.
 *
 * The wire format is the request DTO's json path, dot-separated — exactly what
 * the `query` struct tags corebuilder puts on every DTO field make the Go
 * handlers bind:
 *
 *   { pagination: { limit: 20 } }        → pagination.limit=20
 *   { filters: { search_query: 'wip' } } → filters.search_query=wip
 *   { filters: { statuses: { statuses: ['A','B'] } } }
 *                                        → filters.statuses.statuses=A&…=B
 *   { sort: [{ field: 'due_date' }] }    → sort.0.field=due_date
 *
 * Repeated scalars repeat the key; repeated messages are addressed by index,
 * because that is how the decoder on the other side tells one element from the
 * next. Enums travel as their canonical NAME and timestamps as RFC3339 — both
 * decode through encoding.TextUnmarshaler server-side.
 *
 * Flattening only one level (the behaviour before framework-giph) turned
 * anything deeper into "[object Object]", so structured filters never reached
 * the server at all.
 */
export function appendQueryParam(search: URLSearchParams, key: string, value: unknown): void {
  if (value === undefined || value === null || value === '') return;

  if (value instanceof Date) {
    search.append(key, value.toISOString());
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      if (item !== null && typeof item === 'object' && !(item instanceof Date)) {
        appendQueryParam(search, `${key}.${index}`, item);
      } else {
        appendQueryParam(search, key, item);
      }
    });
    return;
  }

  if (typeof value === 'object') {
    Object.entries(value as Record<string, unknown>).forEach(([childKey, childValue]) => {
      appendQueryParam(search, `${key}.${childKey}`, childValue);
    });
    return;
  }

  search.append(key, String(value));
}

/** Serialize a whole request object into the query string described above. */
export function buildQueryString(params?: QueryParams): string {
  const search = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => appendQueryParam(search, key, value));
  }
  return search.toString();
}

export interface RequestOptions extends RequestInit {
  params?: QueryParams;
  timeout?: number;
  /** Skip authentication header for public endpoints */
  skipAuth?: boolean;
}

export interface RequestContext {
  method: string;
  endpoint: string;
  body?: unknown;
}

export interface ApiError extends Error {
  status: number;
  code?: string;
  details?: unknown;
  requestId?: string;
  requestContext?: RequestContext;
}

class ApiClientError extends Error implements ApiError {
  status: number;
  code?: string;
  details?: unknown;
  requestId?: string;
  requestContext?: RequestContext;

  constructor(
    message: string,
    status: number,
    code?: string,
    details?: unknown,
    requestId?: string,
    requestContext?: RequestContext,
  ) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
    this.requestContext = requestContext;
  }
}

export interface AuthTokens {
  accessToken: string;
  refreshToken?: string;
}

type TokenProvider = () => string | null;
type RefreshTokenProvider = () => string | null;
type TokenRefresher = (refreshToken: string) => Promise<AuthTokens>;
type OnTokenRefreshed = (tokens: AuthTokens) => void;
type OnUnauthorized = () => void;

interface ScopeConfig {
  platformId?: string;
  productId?: string;
  tenantId?: string;
}

type ActiveProfileProvider = () => string | null;

interface ApiClientConfig {
  baseUrl: string;
  scope?: ScopeConfig;
  getAccessToken?: TokenProvider;
  getRefreshToken?: RefreshTokenProvider;
  getActiveProfileId?: ActiveProfileProvider;
  refreshTokens?: TokenRefresher;
  onTokenRefreshed?: OnTokenRefreshed;
  onUnauthorized?: OnUnauthorized;
  defaultTimeout?: number;
}

class ApiClient {
  private baseUrl: string;
  private scope: ScopeConfig;
  private getAccessToken: TokenProvider;
  private getRefreshToken: RefreshTokenProvider;
  private getActiveProfileId: ActiveProfileProvider;
  private refreshTokens: TokenRefresher | null;
  private onTokenRefreshed: OnTokenRefreshed;
  private onUnauthorized: OnUnauthorized;
  private defaultTimeout: number;
  private isRefreshing = false;
  private refreshPromise: Promise<AuthTokens> | null = null;
  private csrfToken: string | null = null;

  constructor(config: ApiClientConfig) {
    this.baseUrl = config.baseUrl.replace(/\/$/, '');
    this.scope = config.scope ?? {};
    this.getAccessToken = config.getAccessToken ?? (() => null);
    this.getRefreshToken = config.getRefreshToken ?? (() => null);
    this.getActiveProfileId = config.getActiveProfileId ?? (() => null);
    this.refreshTokens = config.refreshTokens ?? null;
    this.onTokenRefreshed = config.onTokenRefreshed ?? (() => {});
    this.onUnauthorized = config.onUnauthorized ?? (() => {});
    this.defaultTimeout = config.defaultTimeout ?? 30000;
  }

  private buildUrl(endpoint: string, params?: QueryParams): string {
    const url = new URL(`${this.baseUrl}${endpoint}`, window.location.origin);
    url.search = buildQueryString(params);
    return url.toString();
  }

  private async tryRefreshToken(): Promise<boolean> {
    if (!this.refreshTokens) {
      return false;
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return false;
    }

    // Prevent multiple simultaneous refresh requests
    if (this.isRefreshing) {
      try {
        await this.refreshPromise;
        return true;
      } catch {
        return false;
      }
    }

    this.isRefreshing = true;
    this.refreshPromise = this.refreshTokens(refreshToken);

    try {
      const tokens = await this.refreshPromise;
      this.onTokenRefreshed(tokens);
      return true;
    } catch {
      return false;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestOptions = {},
    isRetry = false
  ): Promise<T> {
    const { params, timeout = this.defaultTimeout, skipAuth = false, ...fetchOptions } = options;

    const url = this.buildUrl(endpoint, params);
    const headers = new Headers(fetchOptions.headers);
    // Don't set Content-Type for FormData — the browser sets multipart/form-data with boundary automatically
    const isFormData = fetchOptions.body instanceof FormData;
    if (!isFormData) {
      headers.set('Content-Type', 'application/json');
    }

    // Add scope headers
    if (this.scope.platformId) {
      headers.set('X-Platform-ID', this.scope.platformId);
    }
    if (this.scope.productId) {
      headers.set('X-Product-ID', this.scope.productId);
    }
    if (this.scope.tenantId) {
      headers.set('X-Tenant-ID', this.scope.tenantId);
    }

    // Add authorization header unless skipAuth is true
    if (!skipAuth) {
      const token = this.getAccessToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
    }

    // Add active profile header (for multi-profile support)
    const activeProfileId = this.getActiveProfileId();
    if (activeProfileId) {
      headers.set('X-Active-Profile', activeProfileId);
    }

    // Add CSRF token for mutating requests
    const method = (fetchOptions.method ?? 'GET').toUpperCase();
    if (this.csrfToken && method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS') {
      headers.set('X-CSRF-Token', this.csrfToken);
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    // Parse request body for error context
    let parsedBody: unknown;
    if (fetchOptions.body && typeof fetchOptions.body === 'string') {
      try { parsedBody = JSON.parse(fetchOptions.body); } catch { parsedBody = undefined; }
    }
    const reqCtx: RequestContext = { method, endpoint, body: parsedBody };

    try {
      const response = await fetch(url, {
        ...fetchOptions,
        headers,
        credentials: 'include',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Store CSRF token from response for subsequent requests
      const responseCsrf = response.headers.get('X-CSRF-Token');
      if (responseCsrf) {
        this.csrfToken = responseCsrf;
      }

      if (!response.ok) {
        // Handle 401 - try to refresh token
        if (response.status === 401 && !isRetry && !skipAuth) {
          const refreshed = await this.tryRefreshToken();
          if (refreshed) {
            // Retry the request with new token
            return this.request<T>(endpoint, options, true);
          }
          // Refresh failed, trigger unauthorized callback
          this.onUnauthorized();
        }

        const requestId = response.headers.get('X-Request-ID') ?? undefined;

        let errorData: { message?: string; code?: string; details?: unknown } = {};
        try {
          errorData = await response.json();
        } catch {
          // Ignore JSON parse errors
        }

        // Handle CSRF rejection: the very first mutating request of a fresh
        // session goes out before any response has delivered a CSRF token.
        // The 403 itself carries a valid X-CSRF-Token (already stored above),
        // so a single retry with it succeeds.
        if (response.status === 403 && errorData.code === 'CSRF_INVALID' && !isRetry && this.csrfToken) {
          return this.request<T>(endpoint, options, true);
        }

        throw new ApiClientError(
          errorData.message ?? `Request failed with status ${response.status}`,
          response.status,
          errorData.code,
          errorData.details,
          requestId,
          reqCtx,
        );
      }

      // Handle empty responses
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        return {} as T;
      }

      const text = await response.text();
      if (!text) {
        return {} as T;
      }

      return JSON.parse(text) as T;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof ApiClientError) {
        throw error;
      }

      if (error instanceof Error && error.name === 'AbortError') {
        throw new ApiClientError('Request timeout', 408, undefined, undefined, undefined, reqCtx);
      }

      throw new ApiClientError(
        error instanceof Error ? error.message : 'Unknown error',
        0,
        undefined,
        undefined,
        undefined,
        reqCtx,
      );
    }
  }

  async get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  async post<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data instanceof FormData ? data : data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data instanceof FormData ? data : data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data instanceof FormData ? data : data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
}

// Singleton instance - will be configured in index.ts
let clientInstance: ApiClient | null = null;

export function initializeApiClient(config: ApiClientConfig): void {
  clientInstance = new ApiClient(config);
}

export function getApiClient(): ApiClient {
  if (!clientInstance) {
    throw new Error('ApiClient not initialized. Call initializeApiClient first.');
  }
  return clientInstance;
}

export { ApiClient, ApiClientError };
